import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { FnmaMenuBarComponent } from '@fm-adk-primeng/components-4';
import { filter } from 'rxjs';
import { CUSTODIAN_ROLES } from '../../../core/constants/iam-roles.constants';
import { AuthService } from '../../../core/services/auth.service';
import { EnvRoleService } from '../../../core/services/env-role.service';

const RESERVE_REQUIREMENT_SUMMARY_PATH = '/custodian/reserve-requirement-summary';

/**
 * SecondaryNavComponent - FRM secondary navigation bar.
 * Requirements: 8.4, 8.5, 8.6; Setup Pulldown Menu Requirements 1.1, 1.2, 1.3
 */
@Component({
  selector: 'app-secondary-nav',
  standalone: true,
  imports: [CommonModule, FnmaMenuBarComponent, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './secondary-nav.component.html',
  styleUrl: './secondary-nav.component.scss'
})
export class SecondaryNavComponent {
  private router = inject(Router);
  private envRoleService = inject(EnvRoleService);
  private authService = inject(AuthService);

  readonly isExternalUser = computed(() => this.authService.isExternalUser?.() ?? false);
  private readonly isCustodian = computed(() => this.authService.hasRole?.(...CUSTODIAN_ROLES) ?? false);
  private readonly currentUrl = signal(this.router.url);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.currentUrl.set(this.router.url));
  }

  /** Fail-closed: hides Admin-only menu items if role resolution is unavailable */
  private isAdmin(): boolean {
    try {
      return this.envRoleService.isAdmin();
    } catch {
      return false;
    }
  }

  /** Returns true when the current URL belongs to any Setup sub-module (Req 1.3) */
  isSetupActive(): boolean {
    const url = this.router.url;
    return url.startsWith('/portfolio-setup') || url.startsWith('/setup/');
  }

  isReserveRequirementSummaryActive(): boolean {
    return this.currentUrl().startsWith(RESERVE_REQUIREMENT_SUMMARY_PATH);
  }

  menuItems = computed(() => {
    if (this.isCustodian()) {
      return this.custodianMenuItems();
    }
    return this.isExternalUser() ? this.externalMenuItems() : this.internalMenuItems();
  });

  private reserveRequirementSummaryItem() {
    return {
      title: 'Reserve Requirement Summary',
      command: () => this.router.navigate([RESERVE_REQUIREMENT_SUMMARY_PATH]),
      ...(this.isReserveRequirementSummaryActive() ? { styleClass: 'active-item' } : {})
    };
  }

  private custodianMenuItems() {
    return [
      {
        title: 'Custodian',
        list: [this.reserveRequirementSummaryItem()],
        icon: 'pi pi-chevron-down',
        iconPosition: 'right'
      }
    ];
  }
  private internalMenuItems() {
    return [
      {
        title: 'FRM Lender Overview',
        list: [
          { title: 'Lender List', command: () => this.router.navigate(['/']) },
          { title: 'Lender Profile Overview', command: () => this.router.navigate(['/lender-overview']) },
        ],
        icon: 'pi pi-chevron-down',
        iconPosition: 'right'
      },
      {
        title: 'Financial Submissions',
        icon: 'pi pi-chevron-down',
        iconPosition: 'right',
        list: [
          { title: 'Lender Information Request Form', command: () => this.router.navigate(['/financial-submissions']) },
          { title: 'Lender Quarterly Financials', command: () => this.router.navigate(['/financial-submissions']) },
          {
            title: 'Capital & Liquidity Standards Compliance Review',
            command: () =>
              this.router.navigate(['/financial-submissions'], { queryParams: { tab: 'cls-quarterly-review' } })
          },
          {
            title: 'Lender Quarterly Attestation',
            command: () =>
              this.router.navigate(['/financial-submissions'], { queryParams: { tab: 'attestation-review' } })
          }
        ]
      },
      {
        title: 'Compliance',
        list: [
          { title: 'Required Amount Review Worksheet', command: () => this.router.navigate(['/compliance/generate-review-worksheet']) },
          {
            title: 'Lender Compliance Review - Status Overview',
            command: () => this.router.navigate(['/compliance/lender-compliance-review-status-overview'])
          },
          { title: 'CLS Compliance - Required Values Review', command: () => this.router.navigate(['/compliance']) },
          { title: 'CLS Compliance - Acceptable Values Review', command: () => this.router.navigate(['/compliance/cls-calc-review']) },
          { title: 'SLL Compliance - Accpetable Values Review', command: () => this.router.navigate(['/compliance']) },
        ],
        icon: 'pi pi-chevron-down',
        iconPosition: 'right'
      },
      {
        title: 'Reports',
        list: [
          { title: 'DUS Capital Compliance', command: () => this.router.navigate(['/reports/dus-capital-compliance']) },
          // command handles the normal click; url gives the link an href so middle-click opens a new tab.
          { title: 'Collateral Monitoring', url: '/reports/daily-collateral-summary', command: () => this.router.navigate(['/reports/daily-collateral-summary']) },
          { title: 'Single Loan Limit Supplement', command: () => this.router.navigate(['/reports/lender-loss-sharing']) },
          { title: 'Lender Concentration Risk', url: '/reports/lender-concentration-risk', command: () => this.router.navigate(['/reports/lender-concentration-risk']) },
        ],
        icon: 'pi pi-chevron-down',
        iconPosition: 'right'
      },
      ...(this.envRoleService.isAdmin() || this.envRoleService.isAnalyst() ? this.custodianMenuItems() : []),
      {
        title: 'Setup',
        icon: 'pi pi-chevron-down',
        iconPosition: 'right',
        list: [
          { title: 'Lender Setup', command: () => this.router.navigate(['/setup/lender-setup']) },
          { title: 'CLS Portfolio Level Setup', command: () => this.router.navigate(['/portfolio-setup']) },
          { title: 'CLS Lender Level Setup', command: () => this.router.navigate(['/setup/cls-lender-setup']) },
          { title: 'CLS Loan Level Setup', command: () => this.router.navigate(['/setup/cls-loan-setup']) },
          { title: 'SLL Rules Setup', command: () => this.router.navigate(['/setup/sll-rules-setup']) },
          { title: 'Configuration Setup', command: () => this.router.navigate(['/setup/configuration']) },
          ...(this.isAdmin()
            ? [{ title: 'Lender Collateral Accounts Setup', command: () => this.router.navigate(['/setup/lender-collateral-accounts']) }]
            : []),
          { title: 'Variable Type Setup', command: () => this.router.navigate(['/setup/variable-type']) },
          { title: 'Agency Credit Rating Setup', command: () => this.router.navigate(['/setup/agency-credit-rating']) },
          { title: 'CLS Rule Derivation Utility', command: () => this.router.navigate(['/setup/cls-rule-derivation']) },
          ...(this.envRoleService.isAdmin()
            ? [{ title: 'Lender Lock/ Unlock', command: () => this.router.navigate(['/setup/lock-unlock-admin']) }]
            : []),
        ],
      }
    ];
  }

  private externalMenuItems() {
    return [
      {
        title: 'FRM Lender Overview',
        list: [
          { title: 'Lender Profile Overview', command: () => this.router.navigate(['/lender-overview/external-lenders']) },
        ],
        icon: 'pi pi-chevron-down',
        iconPosition: 'right'
      },
      {
        title: 'Financial Submissions',
        icon: 'pi pi-chevron-down',
        iconPosition: 'right',
        list: [
          { title: 'Lender Information Request Form', command: () => this.router.navigate(['/financial-submissions']) },
          { title: 'Lender Quarterly Financials', command: () => this.router.navigate(['/financial-submissions']) },
          {
            title: 'Capital & Liquidity Standards Compliance Review',
            command: () =>
              this.router.navigate(['/financial-submissions'], { queryParams: { tab: 'cls-quarterly-review' } })
          },
          {
            title: 'Lender Quarterly Attestation',
            command: () =>
              this.router.navigate(['/financial-submissions'], { queryParams: { tab: 'attestation-review' } })
          }
        ]
      },
      {
        title: 'Reports',
        list: [],
        icon: 'pi pi-chevron-down',
        iconPosition: 'right'
      }
    ];
  }
}
